import React, { useCallback, useEffect, useState } from 'react';
import {
  Smartphone, AlertCircle, CheckCircle2, ChevronRight, RefreshCw, ShieldCheck, ScreenShare,
  Network, MonitorDown, HardDrive, BellRing, Eye, ScrollText,
} from 'lucide-react';
import {
  adminService, getPushMetrics,
  AdminScheduledMessage, PushMetrics, LoginApprovalState, DeviceTrustPolicy,
  RemoteSupportAdminState, InstallAllowlistState, MissingDesktopReport, UploadPolicyAdmin,
  ConsentDocumentsResponse,
} from '../../services/adminService';
import { auditService, AuditAccessListResponse } from '../../services/auditService';
import { Skeleton } from '../Skeleton';
import { AdminPage } from './adminNav';

/**
 * 관리자 콘솔 '한눈에 보기' — 콘솔을 열면 처음 보이는 요약 화면(2026-11 도입).
 *
 * 원칙
 * - **기존 관리자 API만 조합한다**(서버 변경 없음). 칸마다 연결된 메뉴가 있고, 그 메뉴가 보이는 역할일 때만
 *   칸을 그리고 요청도 보낸다 → 권한 없는 403 요청이 나가지 않는다(`canSeePage`가 메뉴 노출 판정과 같은 함수).
 * - 칸마다 따로 불러오고 따로 실패한다. 하나가 죽어도 나머지는 보인다.
 * - 요청은 이 화면이 마운트될 때만 나간다(다른 메뉴를 보는 동안에는 없다).
 * - 기기 승인 대기 수는 콘솔이 사이드 메뉴 배지용으로 이미 받으므로 prop으로 받는다(중복 요청 없음).
 *
 * ⚠ 알림 발송 칸의 `/api/admin/metrics`는 부를 때마다 서버의 이벤트 루프 지연 히스토그램·CPU 측정 창을
 *   초기화한다. '알림 발송 현황' 화면도 10초마다 그렇게 하므로 새 영향은 아니지만, 부하 테스트 중에 콘솔을 열면
 *   계측 창이 끊긴다.
 * ⚠ 알림 발송 실패는 '확인할 것'에 넣지 않았다 — 서버 시작 이후 누적 카운터라 한 번 실패하면 재시작 전까지
 *   영원히 '확인할 것'으로 남는다.
 */

interface AdminOverviewProps {
  canSeePage: (page: AdminPage) => boolean;
  /** 기기 승인 대기 수. null = 아직 모름, 'error' = 불러오지 못함 */
  pendingDevices: number | null | 'error';
  onNavigate: (page: AdminPage) => void;
  /** 새로고침 시 콘솔의 대기 배지도 다시 받는다 */
  onRefreshPending?: () => void;
}

type SourceState<T> = { status: 'loading' | 'ok' | 'error'; data?: T };

/** 한 출처 = 요청 하나. enabled가 false면 요청하지 않고 null을 돌려준다 */
function useSource<T>(enabled: boolean, fetcher: () => Promise<T>, reloadKey: number) {
  const [state, setState] = useState<SourceState<T>>({ status: 'loading' });
  const [retryKey, setRetryKey] = useState(0);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    setState(prev => ({ status: 'loading', data: prev.data }));
    fetcher()
      .then(data => { if (!cancelled) setState({ status: 'ok', data }); })
      .catch(() => { if (!cancelled) setState({ status: 'error' }); });
    return () => { cancelled = true; };
  }, [enabled, fetcher, reloadKey, retryKey]);
  const retry = useCallback(() => setRetryKey(k => k + 1), []);
  return enabled ? { ...state, retry } : null;
}

// fetcher는 모듈 스코프 — useSource의 effect deps가 렌더마다 바뀌지 않게
const fetchScheduled = () => adminService.listScheduledMessages();
const fetchPush = () => getPushMetrics();
const fetchLoginApprovals = () => adminService.getLoginApprovals();
const fetchDevicePolicy = () => adminService.getDevicePolicy().then(r => r.policy);
const fetchRemoteSupport = () => adminService.getRemoteSupport();
const fetchAllowlist = () => adminService.getInstallAllowlist();
const fetchMissingDesktop = () => adminService.getMissingDesktop();
const fetchUploadPolicy = () => adminService.getUploadPolicy();
// 대화 열람 승인 대기(Super Admin) — '감사 로그' 메뉴가 Super Admin 전용이라 그 노출 판정을 빌린다
const fetchAuditAccess = () => auditService.listAccessRequests('all');
const fetchConsentDocs = () => adminService.getConsentDocuments();

const card = 'bg-white rounded-2xl border border-slate-200 overflow-hidden';
const sectionTitle = 'mb-2 text-sm font-semibold text-slate-600';
const fmt = (n: number) => (n > 999 ? '999+' : n.toLocaleString('ko-KR'));

type PillTone = 'on' | 'warn' | 'off';
const PILL_CLASS: Record<PillTone, string> = {
  on: 'bg-brand-100 text-brand-700',
  warn: 'bg-amber-50 text-amber-800',
  off: 'bg-slate-100 text-slate-600',
};
const Pill: React.FC<{ tone: PillTone; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`shrink-0 px-2 py-0.5 text-xs rounded-full font-medium ${PILL_CLASS[tone]}`}>{children}</span>
);

const DEVICE_MODE: Record<DeviceTrustPolicy['mode'], { label: string; tone: PillTone; detail: string }> = {
  off: { label: '꺼짐', tone: 'off', detail: '웹 브라우저로도 로그인할 수 있습니다' },
  observe: { label: '관찰', tone: 'warn', detail: '차단 없이 기기 등록·승인 요청만 받습니다' },
  enforce: { label: '강제 적용', tone: 'on', detail: 'PC 앱과 승인된 모바일 기기만 로그인합니다' },
};

export const AdminOverview: React.FC<AdminOverviewProps> = ({ canSeePage, pendingDevices, onNavigate, onRefreshPending }) => {
  const [reloadKey, setReloadKey] = useState(0);
  const [loadedAt, setLoadedAt] = useState(() => new Date());

  const scheduled = useSource<AdminScheduledMessage[]>(canSeePage('messenger'), fetchScheduled, reloadKey);
  const push = useSource<PushMetrics>(canSeePage('push'), fetchPush, reloadKey);
  const loginApproval = useSource<LoginApprovalState>(canSeePage('loginApprovals'), fetchLoginApprovals, reloadKey);
  const devicePolicy = useSource<DeviceTrustPolicy>(canSeePage('devices'), fetchDevicePolicy, reloadKey);
  const remote = useSource<RemoteSupportAdminState>(canSeePage('remoteSupport'), fetchRemoteSupport, reloadKey);
  const allowlist = useSource<InstallAllowlistState>(canSeePage('deploy'), fetchAllowlist, reloadKey);
  const missing = useSource<MissingDesktopReport>(canSeePage('deploy'), fetchMissingDesktop, reloadKey);
  const upload = useSource<UploadPolicyAdmin>(canSeePage('uploadPolicy'), fetchUploadPolicy, reloadKey);
  const auditAccess = useSource<AuditAccessListResponse>(canSeePage('auditLogs'), fetchAuditAccess, reloadKey);
  const consentDocs = useSource<ConsentDocumentsResponse>(canSeePage('consent'), fetchConsentDocs, reloadKey);

  const refresh = () => {
    setReloadKey(k => k + 1);
    setLoadedAt(new Date());
    onRefreshPending?.();
  };

  // ── 확인할 것 ──
  const showDevices = canSeePage('devices');
  const pendingCount = typeof pendingDevices === 'number' ? pendingDevices : 0;
  const failedNotices = scheduled?.data?.filter(r => r.failedAt).length ?? 0;
  const pendingNotices = scheduled?.data?.filter(r => !r.failedAt).length ?? 0;
  const attentionLoading = (showDevices && pendingDevices === null) || scheduled?.status === 'loading'
    || auditAccess?.status === 'loading';
  const attentionRows: React.ReactNode[] = [];
  if (showDevices && pendingCount > 0) {
    attentionRows.push(
      <AttentionRow
        key="devices"
        icon={<Smartphone size={20} />}
        title={<>기기 승인 대기 <span className="font-extrabold text-brand-700">{fmt(pendingCount)}건</span></>}
        detail="승인하거나 거절할 기기 요청이 있습니다"
        onClick={() => onNavigate('devices')}
      />
    );
  }
  if (showDevices && pendingDevices === 'error' && onRefreshPending) {
    attentionRows.push(
      <ErrorRow key="devices-error" label="기기 승인 대기 수를 불러오지 못했습니다" onRetry={onRefreshPending} />
    );
  }
  if (failedNotices > 0) {
    attentionRows.push(
      <AttentionRow
        key="notices"
        icon={<AlertCircle size={20} />}
        title={<>발송하지 못한 공지 예약 <span className="font-extrabold text-brand-700">{fmt(failedNotices)}건</span></>}
        detail="실패 사유를 확인하고 다시 예약해 주세요"
        onClick={() => onNavigate('messenger')}
      />
    );
  }
  if (scheduled?.status === 'error') {
    attentionRows.push(
      <ErrorRow key="notices-error" label="공지 예약을 불러오지 못했습니다" onRetry={scheduled.retry} />
    );
  }
  const auditPending = auditAccess?.data?.pendingCount ?? 0;
  if (auditPending > 0) {
    attentionRows.push(
      <AttentionRow
        key="audit-access"
        icon={<Eye size={20} />}
        title={<>대화 열람 승인 대기 <span className="font-extrabold text-brand-700">{fmt(auditPending)}건</span></>}
        detail="사유를 확인하고 승인하거나 반려해 주세요"
        onClick={() => onNavigate('audit')}
      />
    );
  }
  if (auditAccess?.status === 'error') {
    attentionRows.push(
      <ErrorRow key="audit-access-error" label="대화 열람 요청을 불러오지 못했습니다" onRetry={auditAccess.retry} />
    );
  }
  if ((consentDocs?.data?.publishErrors?.length ?? 0) > 0) {
    attentionRows.push(
      <AttentionRow
        key="consent-error"
        icon={<ScrollText size={20} />}
        title="이용 고지 설정 오류"
        detail={consentDocs?.data?.currentId ? '새 고지문을 게시하지 못해 이전 버전을 쓰고 있습니다' : '게시된 고지문이 없어 직원에게 동의 창이 뜨지 않습니다'}
        onClick={() => onNavigate('consent')}
      />
    );
  }

  // ── 보안 설정 ──
  const securityTiles: React.ReactNode[] = [];
  if (loginApproval) {
    const d = loginApproval.data;
    securityTiles.push(
      <StatusTile
        key="login"
        icon={<ShieldCheck size={18} />}
        title="로그인 승인제"
        state={loginApproval}
        pill={d && <Pill tone={d.enabled ? 'on' : 'off'}>{d.enabled ? '켜짐' : '꺼짐'}</Pill>}
        detail={d && (d.envKillSwitch ? '서버 설정(.env)으로 꺼져 있습니다' : `승인 명단 ${fmt(d.approvals.length)}명`)}
        onClick={() => onNavigate('loginApprovals')}
      />
    );
  }
  if (devicePolicy) {
    const d = devicePolicy.data;
    const m = d ? DEVICE_MODE[d.mode] ?? DEVICE_MODE.off : null;
    securityTiles.push(
      <StatusTile
        key="device"
        icon={<Smartphone size={18} />}
        title="기기 신뢰"
        state={devicePolicy}
        pill={m && <Pill tone={m.tone}>{m.label}</Pill>}
        detail={d && m && (d.envKillSwitch ? '서버 설정(.env)으로 꺼져 있습니다' : m.detail)}
        onClick={() => onNavigate('devices')}
      />
    );
  }
  if (remote) {
    const p = remote.data?.policy;
    const sub = p && [
      `제어 ${p.controlEnabled ? '허용' : '차단'}`,
      p.fileTransferEnabled !== undefined && `파일 ${p.fileTransferEnabled ? '허용' : '차단'}`,
    ].filter(Boolean).join(' · ');
    securityTiles.push(
      <StatusTile
        key="remote"
        icon={<ScreenShare size={18} />}
        title="원격지원"
        state={remote}
        pill={p && <Pill tone={p.enabled ? 'on' : 'off'}>{p.enabled ? '켜짐' : '꺼짐'}</Pill>}
        detail={p && (p.enabled ? sub : '담당자가 원격지원을 시작할 수 없습니다')}
        onClick={() => onNavigate('remoteSupport')}
      />
    );
  }
  if (allowlist) {
    const d = allowlist.data;
    securityTiles.push(
      <StatusTile
        key="allowlist"
        icon={<Network size={18} />}
        title="설치 허용 IP"
        state={allowlist}
        pill={d && <Pill tone={d.gateEnabled ? 'on' : 'warn'}>{d.gateEnabled ? '켜짐' : '꺼짐'}</Pill>}
        detail={d && (d.gateEnabled
          ? `사내 대역 ${d.baseCidrs.length}개 · 예외 ${d.entries.length}개`
          : '모든 IP에서 PC 앱을 새로 설치할 수 있습니다')}
        onClick={() => onNavigate('deploy')}
      />
    );
  }

  // ── 현황 ──
  const statTiles: React.ReactNode[] = [];
  if (missing) {
    const d = missing.data;
    const total = d?.approvedTotal ?? 0;
    const rate = d && total > 0 ? Math.round(((total - d.missing.length) / total) * 100) : null;
    statTiles.push(
      <StatusTile
        key="install"
        icon={<MonitorDown size={18} />}
        title="PC 앱 설치율"
        state={missing}
        value={d && (rate === null ? '—' : `${rate}%`)}
        detail={d && `미설치 ${fmt(d.missing.length)}명 / 승인 직원 ${fmt(total)}명`}
        onClick={() => onNavigate('deploy')}
      />
    );
  }
  if (scheduled && pendingNotices > 0) {
    statTiles.push(
      <StatusTile
        key="pending-notices"
        icon={<AlertCircle size={18} />}
        title="예약된 공지"
        state={scheduled}
        value={`${fmt(pendingNotices)}건`}
        detail="발송을 기다리는 공지 예약"
        onClick={() => onNavigate('messenger')}
      />
    );
  }
  if (upload) {
    const d = upload.data;
    statTiles.push(
      <StatusTile
        key="upload"
        icon={<HardDrive size={18} />}
        title="첨부 정책"
        state={upload}
        value={d && `파일당 ${d.maxFileSizeMB}MB`}
        detail={d && `한 번에 ${d.maxFiles}개 · 보관 ${d.retentionDays > 0 ? `${d.retentionDays}일` : '기간 없음'}`}
        onClick={() => onNavigate('uploadPolicy')}
      />
    );
  }
  if (push) {
    const d = push.data;
    const failed = d ? Object.values(d.channels).reduce((sum, c) => sum + (c?.failed || 0), 0) : 0;
    const since = d?.since ? new Date(d.since) : null;
    statTiles.push(
      <StatusTile
        key="push"
        icon={<BellRing size={18} />}
        title="알림 발송 실패"
        state={push}
        value={d && `${fmt(failed)}건`}
        detail={d && (since && !isNaN(since.getTime())
          ? `${since.toLocaleString('ko-KR', { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' })} 서버 시작 이후 누적`
          : '서버 시작 이후 누적')}
        onClick={() => onNavigate('push')}
      />
    );
  }

  const showAttention = showDevices || !!scheduled;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs text-slate-500">
          {loadedAt.toLocaleTimeString('ko-KR', { hour: 'numeric', minute: '2-digit' })} 기준
        </span>
        <button
          type="button"
          onClick={refresh}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:text-brand-600 hover:bg-slate-100 rounded-lg transition-colors"
        >
          <RefreshCw size={14} aria-hidden="true" />
          새로고침
        </button>
      </div>

      {showAttention && (
        <section aria-labelledby="admin-ov-attention">
          <h3 id="admin-ov-attention" className={sectionTitle}>확인할 것</h3>
          <div className={card}>
            {attentionRows.length > 0 ? (
              <div className="divide-y divide-slate-100">{attentionRows}</div>
            ) : attentionLoading ? (
              <div className="flex items-center gap-3.5 px-[18px] py-4">
                <Skeleton className="w-10 h-10 rounded-xl" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-40" />
                  <Skeleton className="h-3 w-28" />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3.5 px-[18px] py-4">
                <span className="w-10 h-10 shrink-0 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center">
                  <CheckCircle2 size={20} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="text-[15px] font-semibold text-slate-800">확인할 것이 없습니다</p>
                  <p className="mt-0.5 text-[13px] text-slate-500">
                    {showDevices
                      ? '새 기기 요청이나 발송하지 못한 공지 예약이 생기면 여기에 표시됩니다'
                      : '발송하지 못한 공지 예약이 생기면 여기에 표시됩니다'}
                  </p>
                </div>
              </div>
            )}
          </div>
        </section>
      )}

      {securityTiles.length > 0 && (
        <section aria-labelledby="admin-ov-security">
          <h3 id="admin-ov-security" className={sectionTitle}>보안 설정</h3>
          <div className="grid gap-3 sm:grid-cols-2">{securityTiles}</div>
        </section>
      )}

      {statTiles.length > 0 && (
        <section aria-labelledby="admin-ov-stats">
          <h3 id="admin-ov-stats" className={sectionTitle}>현황</h3>
          <div className="grid gap-3 sm:grid-cols-2">{statTiles}</div>
        </section>
      )}
    </div>
  );
};

/** '확인할 것'의 한 줄 — 홈 AttentionCard의 행과 같은 모양 */
const AttentionRow: React.FC<{
  icon: React.ReactNode;
  title: React.ReactNode;
  detail: string;
  onClick: () => void;
}> = ({ icon, title, detail, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group w-full flex items-center gap-3.5 px-[18px] py-3 text-left hover:bg-slate-50 active:bg-slate-100 transition-colors"
  >
    <span className="w-10 h-10 shrink-0 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center" aria-hidden="true">
      {icon}
    </span>
    <span className="flex-1 min-w-0 flex flex-col">
      <span className="text-[15px] font-semibold text-slate-800">{title}</span>
      <span className="mt-0.5 text-[13px] text-slate-500 truncate">{detail}</span>
    </span>
    <span className="h-9 shrink-0 flex items-center gap-0.5 text-[13px] font-semibold text-slate-700 sm:pl-3.5 sm:pr-2 sm:rounded-[10px] sm:border sm:border-slate-200 sm:bg-white sm:group-hover:border-slate-300 transition-colors">
      <span className="sr-only sm:not-sr-only">보기</span>
      <ChevronRight size={16} aria-hidden="true" className="text-slate-500 sm:text-current" />
    </span>
  </button>
);

const ErrorRow: React.FC<{ label: string; onRetry: () => void }> = ({ label, onRetry }) => (
  <div className="flex items-center justify-between gap-3 px-[18px] py-3">
    <span className="text-sm text-brand-700">{label}</span>
    <button type="button" onClick={onRetry} className="shrink-0 text-xs text-brand-700 underline">
      다시 시도
    </button>
  </div>
);

/** 버튼 안에 둘 스켈레톤 — Skeleton은 div라 버튼(구문 콘텐츠) 안에 넣지 않는다 */
const InlineSkeleton: React.FC<{ className: string }> = ({ className }) => (
  <span aria-hidden="true" className={`inline-block animate-pulse bg-slate-200 ${className}`} />
);

/**
 * 상태·현황 칸. 칸 전체가 버튼이다. 값은 pill(상태) 또는 value(숫자) 중 하나를 쓴다.
 * 실패하면 버튼 대신 '불러오지 못했습니다 · 다시 시도'를 그린다(중첩 버튼 금지).
 */
const StatusTile: React.FC<{
  icon: React.ReactNode;
  title: string;
  state: { status: 'loading' | 'ok' | 'error'; retry: () => void; data?: unknown };
  pill?: React.ReactNode;
  value?: React.ReactNode;
  detail?: React.ReactNode;
  onClick: () => void;
}> = ({ icon, title, state, pill, value, detail, onClick }) => {
  const head = (
    <span className="flex items-center gap-2 min-w-0">
      <span className="text-slate-500 shrink-0" aria-hidden="true">{icon}</span>
      <span className="text-sm font-medium text-slate-800 truncate">{title}</span>
    </span>
  );

  if (state.status === 'error' && state.data === undefined) {
    return (
      <div className="p-4 bg-white border border-slate-200 rounded-xl">
        {head}
        <div className="mt-2 flex items-center justify-between gap-2">
          <span className="text-xs text-brand-700">불러오지 못했습니다</span>
          <button type="button" onClick={state.retry} className="shrink-0 text-xs text-brand-700 underline">
            다시 시도
          </button>
        </div>
      </div>
    );
  }

  const loading = state.data === undefined;
  return (
    <button
      type="button"
      onClick={onClick}
      className="group text-left p-4 bg-white border border-slate-200 rounded-xl hover:border-slate-300 hover:bg-slate-50 transition-colors"
    >
      <span className="flex items-center justify-between gap-2">
        {head}
        {loading ? <InlineSkeleton className="h-5 w-12 rounded-full" /> : pill}
        {!pill && (
          <ChevronRight size={16} aria-hidden="true" className="shrink-0 text-slate-400 group-hover:text-slate-600" />
        )}
      </span>
      {value !== undefined && !loading && (
        <span className="mt-2 block text-xl font-bold text-slate-900 tabular-nums">{value}</span>
      )}
      <span className="mt-1 block text-xs text-slate-500 truncate">
        {loading ? <InlineSkeleton className="h-3 w-32 rounded-md align-middle" /> : detail}
      </span>
    </button>
  );
};
